import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import SecurityIcon from '@mui/icons-material/Security';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const ICONS = [
  { icon: MergeTypeIcon, gradient: 'linear-gradient(135deg, #6366f1, #818cf8)', key: 'merge' },
  { icon: RotateRightIcon, gradient: 'linear-gradient(135deg, #f59e0b, #fbbf24)', key: 'rotate' },
  { icon: SwapVertIcon, gradient: 'linear-gradient(135deg, #10b981, #34d399)', key: 'reorder' },
  { icon: SecurityIcon, gradient: 'linear-gradient(135deg, #ef4444, #f87171)', key: 'secure' },
];

const FeatureCard = ({ icon: Icon, gradient, title, desc, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5, delay: index * 0.1 }}
    whileHover={{ y: -6, transition: { duration: 0.2 } }}
  >
    <Box
      sx={{
        p: 4,
        borderRadius: 4,
        bgcolor: 'background.paper',
        border: '1px solid rgba(99,102,241,0.08)',
        boxShadow: '0 4px 24px -4px rgba(99,102,241,0.08)',
        height: '100%',
        transition: 'box-shadow 0.3s ease',
        '&:hover': { boxShadow: '0 16px 40px -8px rgba(99,102,241,0.18)' },
      }}
    >
      <Box
        sx={{
          width: 56, height: 56, borderRadius: 3,
          background: gradient,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          mb: 2.5, boxShadow: '0 8px 20px -4px rgba(0,0,0,0.15)',
        }}
      >
        <Icon sx={{ color: 'white', fontSize: 28 }} />
      </Box>
      <Typography variant="h6" component="h3" fontWeight={700} gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary" lineHeight={1.7}>{desc}</Typography>
    </Box>
  </motion.div>
);

export default function FeaturesSection() {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        py: { xs: 10, md: 14 },
        background: (theme) => theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #0f0f1a 0%, #13132a 100%)'
          : 'linear-gradient(180deg, #fff 0%, #f8f7ff 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <Typography
            variant="h2"
            align="center"
            sx={{
              mb: 8,
              fontSize: { xs: '2rem', md: '2.8rem' },
              background: (theme) => theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #f1f5f9, #818cf8)'
                : 'linear-gradient(135deg, #111827, #6366f1)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            {t('features.title')}
          </Typography>
        </motion.div>

        <Grid container spacing={3}>
          {ICONS.map(({ icon, gradient, key }, i) => (
            <Grid item xs={12} sm={6} md={3} key={key}>
              <FeatureCard
                icon={icon}
                gradient={gradient}
                title={t(`features.${key}.title`)}
                desc={t(`features.${key}.desc`)}
                index={i}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
